import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";

// ---- helpers ---------------------------------------------------------------
const getActualHours = (log) =>
  parseFloat(
    log?.actual ?? log?.actualHours ?? log?.hours ?? log?.totalHours ?? 0,
  ) || 0;

const getLogDateISO = (log) => {
  const d = log?.date || log?.createdAt;
  return d ? new Date(d).toISOString() : null;
};

const getEmpName = (log) =>
  String(log?.user?.name || log?.employeeName || "Unknown").trim();

const getEmpId = (log) =>
  log?.user?._id || log?.userId || log?.employeeId || "";

const normalizeCategory = (log) => {
  let category = String(log?.projectCategory || "").trim();
  const pname = String(log?.projectName || "").trim();
  const pnameUC = pname.toUpperCase();
  const pnameLC = pname.toLowerCase();

  if (!category) {
    if (pnameUC.startsWith("US_")) category = "USA";
    else if (pnameUC.startsWith("CAN_")) category = "CANADA";
    else if (pnameLC.includes("arch.ind")) category = "Arch.ind";
    else if (pnameLC.includes("gruda")) category = "Gruda";
    else if (pnameLC.includes("dc")) category = "DC";
    else if (pnameLC.includes("projectdata")) category = "ProjectData";
    else if (pnameLC.includes("interior")) category = "Interior";
    else if (pnameLC.includes("bd & m") || pnameLC.includes("bd&m"))
      category = "BD & M";
    else if (pnameLC.includes("finance")) category = "Finance";
    else if (pnameLC.includes("sales")) category = "Sales";
    else if (pnameLC.includes("marketing")) category = "Marketing";
    else category = "Uncategorized";
  } else {
    const lc = category.toLowerCase();
    if (lc === "arch.ind") category = "Arch.ind";
    else if (lc === "gruda") category = "Gruda";
    else if (lc === "usa") category = "USA";
    else if (lc === "canada") category = "CANADA";
    else if (lc === "dc") category = "DC";
    else if (lc === "projectdata") category = "ProjectData";
    else if (lc === "interior") category = "Interior";
    else if (lc === "finance") category = "Finance";
    else if (lc === "sales") category = "Sales";
    else if (lc === "marketing") category = "Marketing";
    else category = category.charAt(0).toUpperCase() + category.slice(1);
  }
  return category;
};

const getMonthLabel = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

// ---- component -------------------------------------------------------------
const EmployeeWorkDetails = ({ user, permissions = {} }) => {
  const navigate = useNavigate();

  const role = String(user?.role || "")
    .trim()
    .toLowerCase();

  const isAdmin = role === "admin";

  const isAccountant = ["accountant", "accoutant", "finance"].includes(role);

  const canViewAllLogs =
    isAdmin || isAccountant || permissions?.employeeLog?.viewAll === true;

  const canViewCost = isAdmin || isAccountant;

  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);

  const [empRates, setEmpRates] = useState({}); // { name: rate }
  const [employeeNames, setEmployeeNames] = useState([]);

  const [projectSummaryByCategory, setProjectSummaryByCategory] = useState({});

  const [selectedMonth, setSelectedMonth] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // ---- fetch ---------------------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem("token") || "";
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const load = async () => {
      try {
        setErrorMessage("");

        const logsPromise = canViewAllLogs
          ? api.get("/logs/all", auth)
          : api.get("/logs/my", auth);

        const usersPromise =
          isAdmin || isAccountant
            ? api.get("/users", auth)
            : Promise.resolve({ data: null });

        const [logsRes, usersRes] = await Promise.all([
          logsPromise,
          usersPromise,
        ]);

        setLogs(Array.isArray(logsRes.data) ? logsRes.data : []);

        if (Array.isArray(usersRes.data)) {
          const map = {};
          usersRes.data.forEach((u) => {
            map[String(u.name || "").trim()] = Number(u.hourlyRate || 0);
          });
          setEmpRates(map);
        } else {
          // Fallback for regular users: use only the current user rate.
          if (user?.name) {
            setEmpRates({
              [String(user.name).trim()]: Number(user.hourlyRate || 0),
            });
          }
        }
      } catch (error) {
        console.error("Failed to fetch employee work details:", error);

        setLogs([]);
        setFilteredLogs([]);

        setErrorMessage(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to fetch employee work details.",
        );
      }
    };

    load();
  }, [canViewAllLogs, isAdmin, isAccountant, user]);

  // ---- month options -------------------------------------------------------
  const monthOptions = useMemo(() => {
    const set = new Set();
    logs.forEach((l) => {
      const iso = getLogDateISO(l);
      if (iso) set.add(getMonthLabel(iso));
    });
    return Array.from(set).sort((a, b) => {
      const [ma, ya] = a.split(" ");
      const [mb, yb] = b.split(" ");
      return new Date(`${mb} 1, ${yb}`) - new Date(`${ma} 1, ${ya}`);
    });
  }, [logs]);

  useEffect(() => {
    if (!selectedMonth && monthOptions.length) {
      const now = new Date();
      const current = now.toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
      });
      setSelectedMonth(
        monthOptions.includes(current) ? current : monthOptions[0],
      );
    }
  }, [monthOptions, selectedMonth]);

  // ---- apply filters -------------------------------------------------------
  useEffect(() => {
    if (!logs.length) return setFilteredLogs([]);

    const withinRange = (iso) => {
      if (!showDateFilter) return true;
      const d = new Date(iso);
      return (
        (!fromDate || d >= new Date(fromDate)) &&
        (!toDate || d <= new Date(toDate))
      );
    };

    const next = logs.filter((l) => {
      const iso = getLogDateISO(l);
      if (!iso) return false;
      if (showDateFilter) return withinRange(iso);
      return selectedMonth ? getMonthLabel(iso) === selectedMonth : true;
    });

    setFilteredLogs(next);
  }, [logs, selectedMonth, showDateFilter, fromDate, toDate]);

  // ---- aggregate summary ---------------------------------------------------
  useEffect(() => {
    const categoryMap = {};
    const allEmpNames = new Set();

    filteredLogs.forEach((log) => {
      const emp = getEmpName(log);
      const project = String(log?.projectName || "Untitled").trim();
      const act = getActualHours(log);
      const category = normalizeCategory(log);

      if (!categoryMap[category]) categoryMap[category] = {};
      if (!categoryMap[category][project]) {
        categoryMap[category][project] = {
          actual: 0,
          employeeHours: {},
          projectCost: 0,
        };
      }

      categoryMap[category][project].actual += act;
      categoryMap[category][project].employeeHours[emp] =
        (categoryMap[category][project].employeeHours[emp] || 0) + act;

      allEmpNames.add(emp);
    });

    // Round hour values and calculate project cost for authorized roles.
    for (const cat in categoryMap) {
      for (const proj in categoryMap[cat]) {
        const node = categoryMap[cat][proj];
        let cost = 0;
        for (const emp in node.employeeHours) {
          const hrs = node.employeeHours[emp];
          const rate = canViewCost ? empRates[emp] || 0 : 0;
          cost += hrs * rate;
          node.employeeHours[emp] = parseFloat(hrs.toFixed(2));
        }
        node.actual = parseFloat(node.actual.toFixed(2));
        node.projectCost = parseFloat(cost.toFixed(2));
      }
    }

    setEmployeeNames(
      Array.from(allEmpNames).sort((a, b) => a.localeCompare(b)),
    );
    setProjectSummaryByCategory(categoryMap);
  }, [filteredLogs, empRates, canViewCost]);

  const activeProjectStats = useMemo(() => {
    const counts = {};
    let total = 0;
    Object.entries(projectSummaryByCategory).forEach(([cat, projs]) => {
      const c = Object.keys(projs).length;
      counts[cat] = c;
      total += c;
    });
    return {
      counts: Object.fromEntries(
        Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0])),
      ),
      total,
    };
  }, [projectSummaryByCategory]);

  const grandTotalsPerEmployee = useMemo(() => {
    const map = {};
    Object.values(projectSummaryByCategory).forEach((projects) => {
      Object.values(projects).forEach((data) => {
        for (const emp in data.employeeHours) {
          map[emp] = (map[emp] || 0) + (data.employeeHours[emp] || 0);
        }
      });
    });
    for (const emp in map) map[emp] = parseFloat(map[emp].toFixed(2));
    return map;
  }, [projectSummaryByCategory]);

  const grandTotal = { hours: 0, cost: 0 };

  // ---- render ---------------------------------------------------------------
  return (
    <div style={styles.page}>
      <h2 style={styles.pageTitle}>Employee Work Details</h2>

      {errorMessage && <div style={styles.errorMessage}>{errorMessage}</div>}

      {/* Filters */}
      <div style={styles.filtersRow}>
        {!showDateFilter && (
          <div style={styles.filterItem}>
            <label style={styles.label}>Month</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={styles.select}
            >
              <option value="">All</option>
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        )}

        <div style={styles.filterItemInline}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showDateFilter}
              onChange={(e) => setShowDateFilter(e.target.checked)}
            />
            <span style={{ marginLeft: 8 }}>Use custom date range</span>
          </label>
        </div>

        {showDateFilter && (
          <>
            <div style={styles.filterItem}>
              <label style={styles.label}>From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                style={styles.input}
              />
            </div>
            <div style={styles.filterItem}>
              <label style={styles.label}>To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                style={styles.input}
              />
            </div>
          </>
        )}
      </div>

      {canViewAllLogs && (
        <div style={styles.card}>
          <h3 style={styles.subHeading}>👥 Employee Log Summary</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Employee</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(
                  filteredLogs.reduce((acc, log) => {
                    const name = getEmpName(log);
                    const id = getEmpId(log);
                    if (name && id && !acc[name]) acc[name] = id;
                    return acc;
                  }, {}),
                ).map(([name, userId]) => (
                  <tr key={userId}>
                    <td style={styles.td}>{name}</td>
                    <td style={styles.td}>
                      <button
                        style={styles.smallButton}
                        onClick={() => navigate(`/admin/logs/${userId}`)}
                      >
                        📊 View Logs
                      </button>
                    </td>
                  </tr>
                ))}
                {!filteredLogs.length && (
                  <tr>
                    <td style={styles.td} colSpan={2}>
                      No logs found for the selected filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Project-wise summary */}
      <h2 style={{ textAlign: "center", marginTop: 10, marginBottom: 10 }}>
        Project-wise Summary
      </h2>

      <div id="project-summary-table" style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th
                style={{
                  ...styles.th,
                  ...styles.stickyHeader,
                  textAlign: "left",
                }}
              >
                Project
              </th>
              <th style={styles.thHeader}>Total</th>
              {canViewCost && <th style={styles.thHeader}>Project Cost</th>}
              {employeeNames.map((emp) => (
                <th key={emp} style={styles.thHeader}>
                  {emp}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(projectSummaryByCategory).map(
              ([category, projects]) => {
                const categoryTotal = {
                  actual: 0,
                  cost: 0,
                  employeeHours: Object.fromEntries(
                    employeeNames.map((e) => [e, 0]),
                  ),
                };

                const projectRows = Object.entries(projects).map(
                  ([project, data], i) => {
                    grandTotal.hours += data.actual;
                    grandTotal.cost += data.projectCost;

                    categoryTotal.actual += data.actual;
                    categoryTotal.cost += data.projectCost;
                    employeeNames.forEach((emp) => {
                      categoryTotal.employeeHours[emp] +=
                        data.employeeHours[emp] || 0;
                    });

                    return (
                      <tr key={`${category}-${i}`}>
                        <td
                          style={{
                            ...styles.stickyCol,
                            border: "1px solid #ddd",
                            background: "#fff",
                          }}
                          title={project}
                        >
                          {project}
                        </td>
                        <td style={styles.td}>{data.actual.toFixed(2)}</td>
                        {canViewCost && (
                          <td style={styles.td}>
                            ₹
                            {data.projectCost.toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                            })}
                          </td>
                        )}
                        {employeeNames.map((emp) => (
                          <td key={emp} style={styles.td}>
                            {data.employeeHours[emp]
                              ? data.employeeHours[emp].toFixed(2)
                              : ""}
                          </td>
                        ))}
                      </tr>
                    );
                  },
                );

                return (
                  <React.Fragment key={category}>
                    <tr
                      style={{ backgroundColor: "#e0e0e0", fontWeight: "bold" }}
                    >
                      <td
                        style={{
                          ...styles.stickyCol,
                          ...styles.stickyGrey,
                          border: "1px solid #ddd",
                        }}
                      >
                        {category.toUpperCase()}
                      </td>
                      <td style={styles.td}>
                        {categoryTotal.actual.toFixed(2)}
                      </td>
                      {canViewCost && (
                        <td style={styles.td}>
                          ₹
                          {categoryTotal.cost.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                          })}
                        </td>
                      )}
                      {employeeNames.map((emp) => (
                        <td key={emp} style={styles.td}>
                          {categoryTotal.employeeHours[emp].toFixed(2)}
                        </td>
                      ))}
                    </tr>
                    {projectRows}
                  </React.Fragment>
                );
              },
            )}

            <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
              <td
                style={{
                  ...styles.stickyCol,
                  ...styles.stickyGrey,
                  border: "1px solid #ddd",
                }}
              >
                Grand Total
              </td>
              <td style={styles.td}>{grandTotal.hours.toFixed(2)}</td>
              {canViewCost && (
                <td style={styles.td}>
                  ₹
                  {grandTotal.cost.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </td>
              )}
              {employeeNames.map((emp) => (
                <td key={emp} style={styles.td}>
                  {grandTotalsPerEmployee[emp]?.toFixed(2) || "0.00"}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* KPI */}
      <div style={styles.card}>
        <h3 style={{ ...styles.subHeading, textAlign: "center" }}>
          📌 Total Number of Active Projects this Period
        </h3>
        <div style={styles.cardsGrid}>
          {Object.entries(activeProjectStats.counts).map(([cat, count]) => (
            <div key={cat} style={styles.kpiCard}>
              <div style={styles.kpiTop}>
                <span style={styles.kpiBadge}>{cat.toUpperCase()}</span>
              </div>
              <div style={styles.kpiValue}>{count}</div>
              <div style={styles.kpiCaption}>Active Projects</div>
            </div>
          ))}
          <div
            style={{ ...styles.kpiCard, background: "#343a76", color: "#fff" }}
          >
            <div style={styles.kpiValueLarge}>{activeProjectStats.total}</div>
            <div
              style={{ ...styles.kpiCaption, color: "rgba(255,255,255,0.9)" }}
            >
              Total Active Projects
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- styles ----------------------------------------------------------------
const styles = {
  page: { display: "flex", flexDirection: "column", gap: 16 },
  pageTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: "#343a76" },
  errorMessage: {
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #f5c2c7",
    background: "#fde7ea",
    color: "#b00020",
    fontSize: 13,
    fontWeight: 600,
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "end",
    background: "#fff",
    padding: "12px 12px 8px",
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
  },
  filterItem: { display: "flex", flexDirection: "column", minWidth: 180 },
  filterItemInline: { display: "flex", alignItems: "center", minWidth: 220 },
  label: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 6,
    color: "#555",
    letterSpacing: 0.3,
  },
  input: {
    height: 36,
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    background: "#fff",
  },
  select: {
    height: 36,
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #ccc",
    background: "#fff",
  },
  checkboxLabel: {
    fontSize: 14,
    color: "#333",
    display: "flex",
    alignItems: "center",
  },

  card: {
    background: "#fff",
    padding: 16,
    borderRadius: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  subHeading: {
    margin: "0 0 10px 0",
    fontSize: 18,
    fontWeight: 700,
    color: "#343a76",
  },

  tableWrapper: {
    overflowX: "auto",
    background: "#fff",
    borderRadius: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: 900,
  },
  th: {
    padding: "10px 12px",
    fontSize: 14,
    background: "#f8f8ff",
    borderBottom: "1px solid #ddd",
    textAlign: "left",
  },
  thHeader: {
    padding: "10px 12px",
    fontSize: 14,
    backgroundColor: "#343a76",
    color: "#fff",
    borderBottom: "1px solid #ddd",
  },
  stickyHeader: {
    position: "sticky",
    left: 0,
    zIndex: 4,
    backgroundColor: "#343a76",
    color: "#fff",
  },
  td: {
    padding: "10px 12px",
    fontSize: 14,
    borderBottom: "1px solid #eee",
    background: "#fff",
    textAlign: "center",
    whiteSpace: "nowrap",
  },
  stickyCol: {
    position: "sticky",
    left: 0,
    zIndex: 3,
    padding: "10px 12px",
    fontSize: 14,
    textAlign: "left",
    background: "#fff",
  },
  stickyGrey: { backgroundColor: "#e0e0e0", zIndex: 3, fontWeight: "bold" },

  smallButton: {
    padding: "6px 10px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid #343a76",
    background: "#fff",
    color: "#343a76",
    cursor: "pointer",
  },

  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 12,
  },
  kpiCard: {
    borderRadius: 12,
    background: "#f7f8ff",
    padding: 14,
    boxShadow: "inset 0 0 0 1px rgba(52,58,118,0.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
  },
  kpiTop: { width: "100%", display: "flex", justifyContent: "flex-start" },
  kpiBadge: {
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 8px",
    borderRadius: 999,
    background: "#343a76",
    color: "#fff",
  },
  kpiValue: { fontSize: 28, fontWeight: 800, lineHeight: 1 },
  kpiValueLarge: { fontSize: 36, fontWeight: 900, lineHeight: 1.1 },
  kpiCaption: { fontSize: 12, color: "#555" },
};

export default EmployeeWorkDetails;
